import { useMemo, useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { UseFormReturn, useForm, useFormState, useWatch, standardSchemaResolver } from '@fiverr-private/forms';
import { useIdentificationContext } from '@fiverr-private/identification_core';
import { isPotentialEmail } from '../../utils/isPotentialEmail/isPotentialEmail';
import { FormSchema, createSignInSchema, createSignUpSchema } from '../../EmailIdentificationStep.constants';
import { appTranslate } from '../../../../i18n';
import { validateEmailForIdentification } from '../../../../services/validation/validateEmailForIdentification';
import { validateUsernameForIdentification } from '../../../../services/validation/validateUsernameForIdentification';
import { globalBigQueryReporter, globalGrafanaReporter, globalMixpanelReporter } from '../../../../events';
import { extractEmailDomain } from '../../../../shared/utils/extractEmailDomain';
import { VALIDATION_DEBOUNCE_TIME } from './useIdentificationStepForm.constants';

interface ValidateArgs {
    value: string;
    setError: UseFormReturn<FormSchema>['setError'];
    clearErrors: UseFormReturn<FormSchema>['clearErrors'];
}

interface Props {
    setLoading: (loading: boolean) => void;
}

type UseFormStateReturn = ReturnType<typeof useFormState<FormSchema>>;

export const useIdentificationStepForm = ({ setLoading }: Props) => {
    const { mode: initialMode } = useIdentificationContext();

    const [isSignUp, setIsSignUp] = useState(initialMode === 'sign_up');

    const schema = useMemo(() => (isSignUp ? createSignUpSchema() : createSignInSchema()), [isSignUp]);

    const methods = useForm<FormSchema>({
        mode: 'onChange',
        resolver: standardSchemaResolver(schema),
        defaultValues: {
            usernameOrEmail: '',
            password: '',
        },
    });

    const { errors } = useFormState<FormSchema>({ control: methods.control, name: 'password' });

    const password = useWatch<FormSchema>({ control: methods.control, name: 'password' });
    const usernameOrEmail = useWatch<FormSchema>({ control: methods.control, name: 'usernameOrEmail' });

    const verifyEmailDebounced = useMemo(() => {
        const verifyEmail = async ({ value, setError, clearErrors }: ValidateArgs) => {
            setLoading(true);
            const result = await validateEmailForIdentification(value);
            setLoading(false);

            if (!result) {
                return;
            }

            const { exists, valid, errorKeys } = result;

            if (exists) {
                setIsSignUp(false);
                clearErrors('usernameOrEmail');
                return;
            }

            if (valid) {
                setIsSignUp(true);
            } else {
                const errorKey = Array.isArray(errorKeys) && errorKeys[0] ? errorKeys[0] : 'email';

                setError('usernameOrEmail', {
                    message: appTranslate<string>(`validation.identification_login.${errorKey}`),
                });
            }
        };

        return debounce(verifyEmail, VALIDATION_DEBOUNCE_TIME);
    }, [setLoading]);

    const verifyUsernameDebounced = useMemo(() => {
        const verifyUsername = async ({ value, setError, clearErrors }: ValidateArgs) => {
            setLoading(true);
            const result = await validateUsernameForIdentification(value);
            setLoading(false);

            if (!result) {
                return;
            }

            const { exists } = result;

            if (exists) {
                setIsSignUp(false);
                clearErrors('usernameOrEmail');
                return;
            }

            setError('usernameOrEmail', {
                message: appTranslate<string>('validation.identification_login.format_username_email'),
            });
        };

        return debounce(verifyUsername, VALIDATION_DEBOUNCE_TIME);
    }, [setLoading]);

    const cleanupDebouncedMethods = useCallback(() => {
        verifyEmailDebounced.cancel();
        verifyUsernameDebounced.cancel();
    }, [verifyEmailDebounced, verifyUsernameDebounced]);

    useEffect(() => {
        const cleanupOnUnmount = () => cleanupDebouncedMethods();

        return cleanupOnUnmount;
    }, [cleanupDebouncedMethods]);

    const onUsernameOrEmailChange = async (value: string) => {
        cleanupDebouncedMethods();

        methods.clearErrors('usernameOrEmail');

        if (!isSignUp) {
            methods.clearErrors('password');
        }

        setLoading(true);

        const usernameOrEmailValid = await methods.trigger('usernameOrEmail');

        if (!usernameOrEmailValid) {
            setLoading(false);
            return;
        }

        const isEmail = isPotentialEmail(value);

        if (isEmail) {
            globalBigQueryReporter.enrichContext({ emailDomain: extractEmailDomain(value) });
            globalMixpanelReporter.enrichContext({ emailDomain: extractEmailDomain(value) });
            await verifyEmailDebounced({
                value,
                setError: methods.setError,
                clearErrors: methods.clearErrors,
            });
        } else {
            setIsSignUp(false);
            await verifyUsernameDebounced({
                value,
                setError: methods.setError,
                clearErrors: methods.clearErrors,
            });
        }
    };

    const onPasswordVisibilityChange = useCallback((isVisible: boolean) => {
        if (isVisible) {
            globalGrafanaReporter.count('identification.%{platform}.email_identification.show_password');
            globalMixpanelReporter.send('Identification - Email Identification - Show Password');
            globalBigQueryReporter.send('identification.email_identification.show_password');
        } else {
            globalGrafanaReporter.count('identification.%{platform}.email_identification.hide_password');
            globalMixpanelReporter.send('Identification - Email Identification - Hide Password');
            globalBigQueryReporter.send('identification.email_identification.hide_password');
        }
    }, []);

    return {
        methods,
        password,
        usernameOrEmail,
        passwordError: errors?.password as UseFormStateReturn['errors']['password'],
        isSignUp,
        onUsernameOrEmailChange,
        onPasswordVisibilityChange,
    };
};
